import React, { useRef, useState, useEffect, useMemo } from "react"
import styled from "styled-components"
import gsap from "gsap"

import colors from "assets/styles/colors"
import text from "assets/styles/text"
import media from "assets/styles/media"

import PlayButton from "components/buttons/PlayButton"

import PLAY from "images/homepage/play.png"
import PAUSE from "images/homepage/pause.png"
import NEXT from "images/homepage/NextArrow.png"
import PREV from "images/homepage/PreviousArrow.png"
import { ReactComponent as CloseSVG } from "images/global/close.svg"

type Props = {
  poster: string
  video: string
  index: number
  scrollTo: Function
  total: number
  name: string
  title: string
  fullscreen: boolean
  setFullscreen: Function
}

const VideoPlayer: React.FC<Props> = ({
  poster,
  video,
  scrollTo,
  index,
  total,
  name,
  title,
  fullscreen,
  setFullscreen,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const wrapperRef = useRef(null)

  const [play, setPlay] = useState<boolean>(false)

  const initialPlay = () => {
    setPlay(true)
    setFullscreen(index)
    enterFullScreen()
  }

  const handleClick = () => {
    if (fullscreen) {
      setPlay(play => !play)
    }
  }

  const handleNext = () => {
    exitFullScreen()
    setPlay(false)
    scrollTo(index + 1)
  }

  const handlePrevious = () => {
    setPlay(false)
    exitFullScreen()
    scrollTo(index - 1)
  }

  const handleClose = () => {
    setFullscreen(false)
    exitFullScreen()
    setPlay(false)
    setFullscreen(false)
  }

  const enterFullScreen = () => {
    window.locomotiveScroll.stop()
    const headerCheck = document.getElementById("header")
    const sideNavCheck = document.getElementById("sidenav-open")
    scrollTo(index)

    gsap.to(videoRef.current, {
      duration: 1,
      width: "100%",
      height: "100%",
      opacity: 1,
    })
    if (headerCheck) {
      gsap.to("#header", {
        duration: 1,
        y: "-200%",
      })
    }
    if (sideNavCheck) {
      gsap.to("#sidenav-open", {
        duration: 1,
        x: "-100%",
      })
    }
  }
  const exitFullScreen = () => {
    window.locomotiveScroll.start()
    const headerCheck = document.getElementById("header")
    const sideNavCheck = document.getElementById("sidenav-open")
    gsap.to(videoRef.current, {
      duration: 1,
      width: "69.861vw",
      height: "100%",
      cursor: "auto",
      opacity: 0.6,
    })
    if (headerCheck) {
      gsap.to("#header", {
        duration: 1,
        y: 0,
      })
    }

    if (sideNavCheck) {
      gsap.to("#sidenav-open", {
        duration: 1,
        x: 0,
      })
    }
  }
  useEffect(() => {
    if (videoRef.current) {
      if (play) {
        if (fullscreen) {
          gsap.set(videoRef.current, {
            cursor: `url(${PAUSE}) 10 22, auto`,
          })
        }
        videoRef.current.play()
      } else {
        videoRef.current.pause()

        if (fullscreen) {
          gsap.set(videoRef.current, {
            cursor: `url(${PLAY}) 12 16, auto`,
          })
        }
      }
    }
  }, [play, fullscreen])

  useEffect(() => {
    if (fullscreen) {
      enterFullScreen()
    } else {
      exitFullScreen()
    }
  }, [fullscreen])

  const videoEl = useMemo(() => {
    return (
      <Video
        id={`video-${index}`}
        ref={videoRef}
        poster={poster}
        onClick={handleClick}
      >
        <source src={video} type="video/mp4" />
      </Video>
    )
  }, [])

  return (
    <Wrapper ref={wrapperRef}>
      {!fullscreen && <PlayButton onClick={initialPlay} />}

      {videoEl}

      {!fullscreen && <Name>{name}</Name>}
      {!fullscreen && <Title>{title}</Title>}

      {fullscreen && (
        <>
          <CloseButton onClick={handleClose}>
            <Close />
          </CloseButton>

          {index > 0 && <PreviousArea onClick={handlePrevious} />}
          {index < total - 1 && <NextArea onClick={handleNext} />}
        </>
      )}
    </Wrapper>
  )
}

export default VideoPlayer

const Wrapper = styled.div`
  height: 100%;
  width: 100vw;
  position: relative;
  filter: grayscale(100);
`

const Video = styled.video`
  object-fit: cover;
  height: 100%;
  width: 69.861vw;
  opacity: 0.6;
`

const AreaStyles = `
  position: absolute;
  z-index: 11;
  width: 13.889vw;
  height: 100%;
  top: 0;
`

const PreviousArea = styled.div`
  ${AreaStyles}
  left: 0;
  cursor: url(${PREV}) 30 30, auto;
`

const NextArea = styled.div`
  ${AreaStyles}
  right: 0;
  cursor: url(${NEXT}) 30 30, auto;
`

const Close = styled(CloseSVG)``

const CloseButton = styled.div`
  position: absolute;
  z-index: 12;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.39vw;
  height: 1.39vw;
  top: 2.778vw;
  right: 2.778vw;
  cursor: pointer;

  svg {
    width: 2.778vw;
    height: 2.778vw;
  }

  &:hover {
    background-color: ${colors.white};

    ${Close} {
      path {
        fill: ${colors.black};
      }
    }
  }
`

const Name = styled.p`
  ${text.desktop.h3}
  color: ${colors.white};
  position: absolute;
  z-index: 2;
  left: 50.972vw;
  top: 20.833vw;
  font-size: 4.167vw;
  width: 45.139vw;
  line-height: 4.583vw;
`

const Title = styled.p`
  ${text.desktop.h5}
  color: ${colors.blue100};
  position: absolute;
  z-index: 2;
  left: 60.347vw;
  bottom: 21.111vw;

  ${media.fullWidth} {
    ${text.fullWidth.h5}
    bottom: 400px;
    right: 300px;
  }
`
